import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import { useServicesList } from '../hooks/useServicesList';

const Header = () => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const location = useLocation();
  const { services } = useServicesList();
  const dropdownRef = useRef(null);

  const isServicesPage = location.pathname.startsWith('/services');
  const isHomePage = location.pathname === '/';
  const isContactPage = location.pathname === '/contact';
  const isAboutPage = location.pathname === '/about';
  const isPortfolioPage = location.pathname === '/portfolio' || location.pathname.startsWith('/project/');
  const isBlogsPage = location.pathname === '/blogs' || location.pathname.startsWith('/blog/');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };
    const handleVisibilityOrBlur = () => setIsServicesOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('visibilitychange', handleVisibilityOrBlur);
    window.addEventListener('blur', handleVisibilityOrBlur);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('visibilitychange', handleVisibilityOrBlur);
      window.removeEventListener('blur', handleVisibilityOrBlur);
    };
  }, []);

  const handleServicesKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setIsServicesOpen(true);
    }
  };

  const handleNavItemClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleServicesClick = (e) => {
    if (isMobileView) {
      if (!isServicesOpen) {
        e.preventDefault();
        setIsServicesOpen(true);
        return;
      }
      setIsServicesOpen(false);
      handleNavItemClick();
      return;
    }
    setIsServicesOpen(false);
    handleNavItemClick();
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <Link to="/" className="logo">
          <img src="/logo_secondary.png" alt="IFSTATIC Technologies" className="logo-image" />
        </Link>
        
        <button 
          className="hamburger-menu"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
        </button>
        
        <nav className={`nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <Link
            to="/"
            className={`nav-link ${isHomePage && !isContactPage ? 'active' : ''}`}
            onClick={handleNavItemClick}
          >
            Home
          </Link>
          <div
            ref={dropdownRef}
            className="nav-dropdown"
            onMouseEnter={() => setIsServicesOpen(true)}
            onMouseLeave={() => setIsServicesOpen(false)}
          >
            <Link
              to="/services"
              className={`nav-link dropdown-toggle ${isServicesPage && !isContactPage ? 'active' : ''}`}
              onClick={handleServicesClick}
              onKeyDown={handleServicesKeyDown}
            >
              Services
              <span className="dropdown-arrow">▼</span>
            </Link>
            {isServicesOpen && (
              <div
                className="dropdown-menu"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
                onClick={(e) => e.stopPropagation()}
              >
                {services.map((service) => (
                  <Link
                    key={service.id || service.alias}
                    to={`/services/${service.alias}`}
                    className="dropdown-item"
                    onClick={() => {
                      setIsServicesOpen(false);
                      handleNavItemClick();
                    }}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/portfolio"
            className={`nav-link ${isPortfolioPage && !isContactPage ? 'active' : ''}`}
            onClick={handleNavItemClick}
          >
            Portfolio
          </Link>
          <Link
            to="/blogs"
            className={`nav-link ${isBlogsPage && !isContactPage ? 'active' : ''}`}
            onClick={handleNavItemClick}
          >
            Blogs
          </Link>
          <Link
            to="/about"
            className={`nav-link ${isAboutPage && !isContactPage ? 'active' : ''}`}
            onClick={handleNavItemClick}
          >
            About us
          </Link>
          <Link to="/contact" className="nav-link mobile-contact" onClick={handleNavItemClick}>Contact</Link>
        </nav>
        <Link to="/contact" className="contact-btn desktop-only" onClick={handleNavItemClick}>Contact</Link>
      </div>
    </header>
  );
};

export default Header;
